import * as Sentry from '@sentry/nextjs';
import {PropsWithChildren} from 'react';
import {ErrorBoundary as ReactErrorBoundary, FallbackProps} from 'react-error-boundary';
import {AnalyticsEvent} from 'redux/types/analytics';
import {Track} from 'components/Track';

type ErrorBoundaryProps = PropsWithChildren<{
    errorEvent?: AnalyticsEvent;
    FallbackComponent?: React.ComponentType<FallbackProps>;
}>;

async function onError(error: Error, info: {componentStack: string}) {
    Sentry.configureScope((scope) => {
        scope.setExtra('componentStack', info.componentStack);
    });
    Sentry.captureException(error);
    await Sentry.flush();
}

function Fallback({
    errorEvent,
    FallbackComponent,
    ...props
}: FallbackProps & {
    errorEvent?: AnalyticsEvent;
    FallbackComponent?: React.ComponentType<FallbackProps>;
}) {
    if (!FallbackComponent) return null;

    return (
        <Track event={errorEvent ?? AnalyticsEvent.GENERAL_ERROR_EVENT}>
            <FallbackComponent {...props} />
        </Track>
    );
}

export function ErrorBoundary({children, errorEvent, FallbackComponent}: ErrorBoundaryProps) {
    return (
        <ReactErrorBoundary
            FallbackComponent={(props) => (
                <Fallback {...props} FallbackComponent={FallbackComponent} errorEvent={errorEvent} />
            )}
            onError={onError}
        >
            {children}
        </ReactErrorBoundary>
    );
}
